import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../Components/Common/Navbar";
import ShowStatsSection from "../Components/Common/ShowStatsSection";
import InteractiveMap from "../Components/Map/Map";
import ChartStatisticsPanel from "../Components/createChart/ChartStatisticsPanel";
import backgroundImg from "../Assets/Images/background.png";
import studies from "../Data/studies.json";
import variablesData from "../Data/cdem_variables.json";
import { getDbSettings } from "../utils/dbConfig";
import { AiOutlineHome } from "react-icons/ai";
import { fetchAllSurveyData, fetchDemocracyCheckupData, isRequestAborted } from "../utils/api";
import { findStudyByName, findStudyCategory } from "../utils/studies";
import {
  normalizeRidingLookupKey,
  normalizeRegionId,
  parseRegionIdAsNumber,
  toRidingIdLookupKey,
  toRidingNameLookupKey,
} from "../utils/regionLookup";
import {
  buildGroupsForPrefix,
  buildQuestionsIndexForPrefix,
  detectNumericScale,
  yearToPrefix,
} from "../Components/createChart/utils/chartHelpers";
import { computeStatistics } from "../Components/Statistics/utils";

const provinceIdToName = {
  1: "Alberta",
  2: "British Columbia",
  3: "Manitoba",
  4: "New Brunswick",
  5: "Newfoundland and Labrador",
  6: "Northwest Territories",
  7: "Nova Scotia",
  8: "Nunavut",
  9: "Ontario",
  10: "Prince Edward Island",
  11: "Quebec",
  12: "Saskatchewan",
  13: "Yukon",
};

const toReadableLabel = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  if (text === text.toLowerCase()) {
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return text;
};

const normalizeEndpointLabel = (value, fallback) => {
  const normalized = String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.;,:-]+$/, "");
  return normalized || fallback;
};

const extractScaleEndpoints = (question) => {
  const text = String(question || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;

  const explicitMatch = text.match(
    /0\s*(?:means|=)\s*([^.;,]+?)\s*(?:and|,)\s*100\s*(?:means|=)\s*([^.;,]+)/i
  );
  if (explicitMatch) {
    return {
      low: normalizeEndpointLabel(explicitMatch[1], "lowest possible rating"),
      high: normalizeEndpointLabel(explicitMatch[2], "highest possible rating"),
    };
  }

  return null;
};

const extractFednumFromDescription = (description = "") => {
  const doc = new DOMParser().parseFromString(String(description || ""), "text/html");
  const cell = [...doc.querySelectorAll("tr td:first-child")].find(
    (td) => String(td?.textContent || "").trim() === "FEDNUM"
  );
  return cell ? cell.nextElementSibling?.textContent : null;
};

const resolveRegionKeys = (selectedFile, rows = [], datasetKey = null) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  const allKeys = new Set();
  normalizedRows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    Object.keys(row).forEach((key) => allKeys.add(key));
  });
  const keys = Array.from(allKeys);

  const existsInAny = (key) =>
    normalizedRows.some((r) => r && r[key] !== undefined && r[key] !== null && r[key] !== "");
  const numericInAny = (key) =>
    normalizedRows.some((row) => normalizeRegionId(row?.[key]) !== null);
  const textInAny = (key) =>
    normalizedRows.some((row) => {
      const raw = row?.[key];
      if (raw === null || raw === undefined) return false;
      const str = String(raw).trim();
      return str.length > 0;
    });
  const pickFirst = (candidates, predicate) =>
    candidates.filter(Boolean).find((field) => existsInAny(field) && predicate(field));
  const unique = (values) => Array.from(new Set(values.filter(Boolean)));
  const isStrongRidingIdFieldName = (field) =>
    /(feduid|fednum|district(_)?id|riding(_)?id|constituency(_)?id|electoral.*(id|code)|edid|ward(_)?id)$/i.test(
      String(field || "")
    );
  const idProfileCache = new Map();
  const getIdFieldProfile = (key) => {
    if (idProfileCache.has(key)) return idProfileCache.get(key);

    let nonEmptyCount = 0;
    let largeCount = 0;
    const ids = new Set();

    normalizedRows.forEach((row) => {
      const parsed = parseRegionIdAsNumber(row?.[key]);
      if (!Number.isFinite(parsed)) return;
      nonEmptyCount += 1;
      ids.add(parsed);
      if (parsed >= 1000) largeCount += 1;
    });

    const uniqueCount = ids.size;
    const uniqueRatio = nonEmptyCount > 0 ? uniqueCount / nonEmptyCount : 0;
    const largeRatio = nonEmptyCount > 0 ? largeCount / nonEmptyCount : 0;
    const profile = { nonEmptyCount, uniqueCount, uniqueRatio, largeRatio };
    idProfileCache.set(key, profile);
    return profile;
  };
  const hasLargeNumericInAny = (key) =>
    normalizedRows.some((row) => {
      const parsed = parseRegionIdAsNumber(row?.[key]);
      return Number.isFinite(parsed) && parsed >= 1000;
    });
  const countTextValues = (key) =>
    normalizedRows.reduce((count, row) => {
      const raw = row?.[key];
      if (raw === null || raw === undefined) return count;
      return String(raw).trim() ? count + 1 : count;
    }, 0);
  const yy = selectedFile?.year ? String(selectedFile.year).slice(-2) : null;
  const isDemocracyCheckup = datasetKey === "democracy_checkup";
  const explicitProvinceField = yy ? `dc${yy}_province` : null;
  const explicitRidingField = yy ? `dc${yy}_feduid` : null;

  // For Democracy Checkup, always prioritize explicit region keys from schema:
  // dcYY_province (province view) and dcYY_feduid (riding view).
  let provinceField = selectedFile?.provinceKey || null;
  if (!provinceField && explicitProvinceField && existsInAny(explicitProvinceField)) {
    provinceField = explicitProvinceField;
  }
  if (!provinceField) {
    const provFallbacks = [
      `dc${yy || ""}_province`,
      "dc_province",
      "province",
      "prov",
      "province_id",
    ];
    provinceField = provFallbacks.find(existsInAny) || provFallbacks[0];
  }

  const ridingGeoKeys = keys.filter((key) =>
    /(feduid|fednum|riding|district|constituency|electoral|edid|ward|enname|frname|ed_name)/i.test(
      key
    )
  );
  const numericHighCardinalityKeys = keys.filter((key) => {
    if (!existsInAny(key) || !numericInAny(key)) return false;
    const profile = getIdFieldProfile(key);
    if (profile.nonEmptyCount < 20) return false;
    if (profile.uniqueCount < 10) return false;
    if (profile.uniqueRatio > 0.9) return false;
    return profile.largeRatio >= 0.5 || profile.uniqueCount >= 40;
  });
  const ridingIdPriority = [
    selectedFile?.ridingIdKey,
    yy ? `dc${yy}_feduid` : null,
    yy ? `dc${yy}_fednum` : null,
    yy ? `dc${yy}_district_id` : null,
    yy ? `dc${yy}_riding_id` : null,
    yy ? `dc${yy}_edid` : null,
    yy ? `dc${yy}_district` : null,
    yy ? `dc${yy}_riding` : null,
    "feduid",
    "FEDUID",
    "fednum",
    "FEDNUM",
    "district_id",
    "riding_id",
    "edid",
    "EDID",
    "district",
    "riding",
  ];
  const ridingIdRegex =
    /(fed(uid|num)|district(_)?id|riding(_)?id|constituency(_)?id|electoral.*(id|code)|edid|ward(_)?id)/i;
  const ridingNamePriority = [
    selectedFile?.ridingNameKey,
    yy ? `dc${yy}_riding_name` : null,
    yy ? `dc${yy}_district_name` : null,
    yy ? `dc${yy}_enname` : null,
    yy ? `dc${yy}_ed_name` : null,
    "ENNAME",
    "enname",
    "ED_NAME",
    "district_name",
    "riding_name",
    "district",
    "riding",
    "name",
  ];
  const ridingNameRegex =
    /(enname|frname|ed_name|riding.*name|district.*name|constituency.*name|electoral.*name)/i;
  const nameTokenRegex = /(riding|district|constituency|electoral|name|enname|frname|ed_name)/i;

  const rankedRidingIdFields = unique([
    isDemocracyCheckup && explicitRidingField ? explicitRidingField : null,
    ...ridingIdPriority,
    ...ridingGeoKeys.filter((key) => ridingIdRegex.test(key)),
    ...ridingGeoKeys,
    ...numericHighCardinalityKeys,
  ])
    .filter((field) => existsInAny(field) && numericInAny(field))
    .filter((field) => {
      const profile = getIdFieldProfile(field);
      if (profile.nonEmptyCount < 20) return false;
      if (profile.uniqueCount < 10) return false;
      if (profile.uniqueRatio > 0.9) return false;
      return (
        isStrongRidingIdFieldName(field) ||
        hasLargeNumericInAny(field) ||
        profile.largeRatio >= 0.5 ||
        profile.uniqueCount >= 40
      );
    })
    .sort((fieldA, fieldB) => {
      const lowerA = String(fieldA).toLowerCase();
      const lowerB = String(fieldB).toLowerCase();
      const profileA = getIdFieldProfile(fieldA);
      const profileB = getIdFieldProfile(fieldB);
      const scoreA =
        (profileA.largeRatio >= 0.5 ? 1500 : 0) +
        (/fed(uid|num)/i.test(lowerA) ? 500 : 0) +
        (/riding|district|constituency|electoral|edid/i.test(lowerA) ? 100 : 0) +
        profileA.uniqueCount -
        Math.round(profileA.uniqueRatio * 100);
      const scoreB =
        (profileB.largeRatio >= 0.5 ? 1500 : 0) +
        (/fed(uid|num)/i.test(lowerB) ? 500 : 0) +
        (/riding|district|constituency|electoral|edid/i.test(lowerB) ? 100 : 0) +
        profileB.uniqueCount -
        Math.round(profileB.uniqueRatio * 100);
      return scoreB - scoreA;
    });

  const rankedRidingNameFields = unique([
    ...ridingNamePriority,
    ...ridingGeoKeys.filter((key) => ridingNameRegex.test(key)),
    ...ridingGeoKeys.filter((key) => nameTokenRegex.test(key)),
  ])
    .filter((field) => existsInAny(field) && textInAny(field))
    .sort((fieldA, fieldB) => countTextValues(fieldB) - countTextValues(fieldA));

  const ridingIdField =
    rankedRidingIdFields[0] ||
    pickFirst(
      ridingGeoKeys.filter((key) => ridingIdRegex.test(key)),
      numericInAny
    ) ||
    null;
  const ridingNameField = rankedRidingNameFields[0] || null;

  if (isDemocracyCheckup) {
    const preferredRidingIdField =
      (explicitRidingField && existsInAny(explicitRidingField) && numericInAny(explicitRidingField)
        ? explicitRidingField
        : null) || ridingIdField;

    const ridingIdFieldCandidates = unique([
      preferredRidingIdField,
      ...rankedRidingIdFields.filter((field) => field !== preferredRidingIdField),
    ]);

    return {
      provinceField,
      ridingIdField: preferredRidingIdField,
      ridingNameField,
      ridingIdFieldCandidates,
      ridingNameFieldCandidates: rankedRidingNameFields,
      ridingCandidateKeys: unique([...ridingGeoKeys, ...numericHighCardinalityKeys]),
    };
  }

  return {
    provinceField,
    ridingIdField,
    ridingNameField,
    ridingIdFieldCandidates: rankedRidingIdFields,
    ridingNameFieldCandidates: rankedRidingNameFields,
    ridingCandidateKeys: unique([...ridingGeoKeys, ...numericHighCardinalityKeys]),
  };
};

const sortMappingEntries = (mapping = {}) =>
  Object.entries(mapping).sort(([leftKey], [rightKey]) => {
    const leftNum = Number(leftKey);
    const rightNum = Number(rightKey);
    if (Number.isFinite(leftNum) && Number.isFinite(rightNum)) return leftNum - rightNum;
    return String(leftKey).localeCompare(String(rightKey));
  });

const ViewInteractiveMapPage = () => {
  /* ── state ──────────────────────────────────────────────*/
  const [ansMap, setAnsMap] = useState({}); // answers for chosen DEP {id:{Display}}

  // STUDY replaces "year"
  const [selectedFile, setSelectedFile] = useState(null); // from studies.json
  const [dbName, setDbName] = useState(null); // resolved DB name

  const [dep, setDep] = useState("");
  const [useWeightedData, setUseWeightedData] = useState(false);

  const [provCounts, setProvCounts] = useState([]); // [{province, total, byResp, freq}]
  const [ridCounts, setRidCounts] = useState([]); // [{riding, total, byResp, freq}]

  const [view, setView] = useState("province");
  const [showStats, setShowStats] = useState(false);
  const [selectedIndepBucket, setSelectedIndepBucket] = useState("All");
  const [sortBy, setSortBy] = useState("label_asc");
  const [loading, setLoading] = useState(false);
  const [ridingNameById, setRidingNameById] = useState({});
  const [ridingNameByLookupKey, setRidingNameByLookupKey] = useState({});

  // find the category record for the chosen study
  const category = findStudyCategory(studies, selectedFile);
  const isDemocracyCheckup = category?.datasetKey === "democracy_checkup";
  const shouldUseDemocracyCheckupEndpoint = useMemo(() => {
    if (isDemocracyCheckup) return true;
    if (/^\d{4}_democracy_checkup(?:_clean)?$/i.test(String(dbName || "").trim())) return true;
    return /democracy checkup/i.test(String(selectedFile?.name || ""));
  }, [isDemocracyCheckup, dbName, selectedFile]);
  const studyPrefix = useMemo(
    () => (selectedFile?.year ? yearToPrefix(selectedFile.year) : null),
    [selectedFile]
  );

  const questionsIndex = useMemo(
    () => (studyPrefix ? buildQuestionsIndexForPrefix(studyPrefix) : {}),
    [studyPrefix]
  );
  const depGroups = useMemo(
    () => (studyPrefix ? buildGroupsForPrefix(variablesData?.Dependent, studyPrefix) : []),
    [studyPrefix]
  );
  const flatDepOptions = useMemo(
    () => depGroups.flatMap((group) => group.options || []),
    [depGroups]
  );
  const selectedDepMeta = useMemo(
    () => flatDepOptions.find((option) => option.id === dep) || null,
    [flatDepOptions, dep]
  );
  const isCategoricalOutcome = String(selectedDepMeta?.type || "").toLowerCase() === "cat";
  const weightColumn = useMemo(() => {
    if (!selectedFile?.year) return null;
    const yearSuffix = String(selectedFile.year).slice(-2).padStart(2, "0");
    return `dc${yearSuffix}_quota_weight`;
  }, [selectedFile]);
  const orderedResponseOptions = useMemo(
    () =>
      sortMappingEntries(ansMap).map(([answerId, value]) => ({
        code: String(answerId),
        label: String(value?.Display || answerId),
      })),
    [ansMap]
  );
  const outcomeResponseEntries = useMemo(
    () => orderedResponseOptions.map((option) => option.label),
    [orderedResponseOptions]
  );
  const outcomeQuestionText = useMemo(
    () => (dep ? questionsIndex[dep]?.mainQuestion || "" : ""),
    [dep, questionsIndex]
  );
  const numericOutcomeGuide = useMemo(() => {
    if (String(selectedDepMeta?.type || "").toLowerCase() !== "num") return null;

    const numericValues = outcomeResponseEntries
      .map((value) => Number(String(value).trim()))
      .filter((value) => Number.isFinite(value));
    if (!numericValues.length) return null;

    const uniqueValues = new Set(numericValues);
    const minValue = Math.min(...numericValues);
    const maxValue = Math.max(...numericValues);
    const looksLikeHundredScale = minValue === 0 && maxValue === 100 && uniqueValues.size >= 50;
    if (!looksLikeHundredScale) return null;

    const extracted = extractScaleEndpoints(outcomeQuestionText);
    return {
      scaleLabel: "0-100 scale",
      lowLabel: extracted?.low || "lowest possible rating",
      highLabel: extracted?.high || "highest possible rating",
    };
  }, [selectedDepMeta?.type, outcomeResponseEntries, outcomeQuestionText]);
  const activeRegionCount = useMemo(
    () => (view === "province" ? provCounts.length : ridCounts.length),
    [view, provCounts.length, ridCounts.length]
  );

  useEffect(() => {
    if (!activeRegionCount && showStats) {
      setShowStats(false);
    }
  }, [activeRegionCount, showStats]);

  /* ── 0. resolve DB name for the selected study ────────────────────────────*/
  useEffect(() => {
    if (!selectedFile) {
      setDbName(null);
      return;
    }

    if (!category) {
      // Fallback: studies in this app are currently Democracy Checkup files,
      // so use the conventional table name when category resolution is missing.
      setDbName(`${selectedFile.year}_democracy_checkup_clean`);
      return;
    }

    const settings = getDbSettings(category.datasetKey, {
      year: selectedFile.year,
      provinceKey: selectedFile.provinceKey,
    });
    setDbName(settings?.dbName || `${selectedFile.year}_democracy_checkup_clean`);
  }, [selectedFile, category]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    fetch("/riding.json", { signal: controller.signal })
      .then((response) => response.json())
      .then((geojson) => {
        if (!isMounted) return;
        const idLookup = {};
        const keyLookup = {};

        (geojson?.features || []).forEach((feature) => {
          const properties = feature?.properties || {};
          const ridingId = parseRegionIdAsNumber(
            properties.feduid ??
              properties.FEDUID ??
              properties.fednum ??
              properties.FEDNUM ??
              extractFednumFromDescription(properties.description)
          );
          const ridingName = String(
            properties.name ?? properties.ED_NAME ?? properties.ENNAME ?? ""
          ).trim();
          if (!ridingName) return;

          if (Number.isFinite(ridingId)) {
            idLookup[String(ridingId)] = ridingName;
          }

          const idKey = toRidingIdLookupKey(ridingId);
          if (idKey) keyLookup[idKey] = ridingName;
          [properties.name, properties.ED_NAME, properties.ENNAME].forEach((nameValue) => {
            const nameKey = toRidingNameLookupKey(nameValue);
            if (nameKey) keyLookup[nameKey] = ridingName;
          });
        });

        setRidingNameById(idLookup);
        setRidingNameByLookupKey(keyLookup);
      })
      .catch(() => {
        if (!isMounted) return;
        setRidingNameById({});
        setRidingNameByLookupKey({});
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  /* ── 1. rebuild answer mapping when DEP changes ───────────────────────────*/

  useEffect(() => {
    const meta = dep ? questionsIndex[dep] : null;
    setAnsMap(meta?.answersMapping || {});
  }, [dep, questionsIndex]);

  useEffect(() => {
    setSelectedIndepBucket("All");
    setSortBy("label_asc");
  }, [dep, view, selectedFile?.name]);

  /* ── 3. fetch rows ➜ aggregate counts per region & per response ──────────*/
  useEffect(() => {
    if (!selectedFile || !dbName) {
      setProvCounts([]);
      setRidCounts([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();
    setLoading(true);

    const fetchRows = shouldUseDemocracyCheckupEndpoint
      ? fetchDemocracyCheckupData(selectedFile?.year, { signal: controller.signal })
      : fetchAllSurveyData(dbName, { signal: controller.signal });

    fetchRows
      .then((data) => {
        if (!isMounted || !Array.isArray(data)) return;
        const {
          provinceField,
          ridingIdField,
          ridingNameField,
          ridingIdFieldCandidates,
          ridingNameFieldCandidates,
        } = resolveRegionKeys(
          selectedFile,
          data,
          shouldUseDemocracyCheckupEndpoint ? "democracy_checkup" : category?.datasetKey
        );
        const selectedRidingIdFields =
          ridingIdFieldCandidates?.length > 0
            ? ridingIdFieldCandidates.slice(0, 3)
            : ridingIdField
              ? [ridingIdField]
              : [];
        const selectedRidingNameFields =
          ridingNameFieldCandidates?.length > 0
            ? ridingNameFieldCandidates.slice(0, 2)
            : ridingNameField
              ? [ridingNameField]
              : [];

        /* A) ignore missing / refused answers (-99) for DEP only if dep selected */
        const validRows = dep
          ? data.filter(
              (row) =>
                row[dep] !== -99 &&
                row[dep] !== "-99" &&
                row[dep] !== null &&
                row[dep] !== undefined &&
                row[dep] !== ""
            )
          : data;
        const getRowWeight = (row) => {
          if (!useWeightedData || !weightColumn) return 1;
          const parsedWeight = Number(row?.[weightColumn]);
          if (!Number.isFinite(parsedWeight)) return 1;
          return Math.max(0, parsedWeight);
        };

        /* B) build aggregates */
        const prov = {};
        const rid = {};

        validRows.forEach((row) => {
          const rowWeight = getRowWeight(row);
          const provinceId = parseRegionIdAsNumber(row?.[provinceField]);
          const ridingIdLookupKeys = selectedRidingIdFields
            .map((field) => toRidingIdLookupKey(row?.[field]))
            .filter(Boolean);
          const ridingNameLookupKeys = selectedRidingNameFields
            .map((field) => toRidingNameLookupKey(row?.[field]))
            .filter(Boolean);
          const ridingNameDisplay = selectedRidingNameFields
            .map((field) => {
              const raw = row?.[field];
              if (raw === null || raw === undefined) return null;
              const normalized = String(raw).trim();
              return normalized || null;
            })
            .find(Boolean);
          const primaryRidingIdLookupKey = ridingIdLookupKeys[0] || null;
          const primaryRidingNameLookupKey = ridingNameLookupKeys[0] || null;
          const ridingId = primaryRidingIdLookupKey
            ? parseRegionIdAsNumber(primaryRidingIdLookupKey.replace(/^id:/, ""))
            : null;

          // province bucket (skip missing)
          if (provinceId !== null) {
            if (!prov[provinceId]) prov[provinceId] = { total: 0, byResp: {} };
            prov[provinceId].total += rowWeight;
            if (dep) {
              const ans = String(row[dep]);
              prov[provinceId].byResp[ans] = (prov[provinceId].byResp[ans] || 0) + rowWeight;
            }
          }

          // riding bucket: support matching by numeric id and fallback by normalized name
          if (primaryRidingIdLookupKey || primaryRidingNameLookupKey) {
            const primaryKey = primaryRidingIdLookupKey || primaryRidingNameLookupKey;
            if (!rid[primaryKey]) {
              rid[primaryKey] = {
                riding: ridingId,
                ridingName: ridingNameDisplay,
                total: 0,
                byResp: {},
                lookupKeys: new Set(),
              };
            }

            const bucket = rid[primaryKey];
            if (!bucket.ridingName && ridingNameDisplay) {
              bucket.ridingName = ridingNameDisplay;
            }
            bucket.total += rowWeight;
            ridingIdLookupKeys.forEach((lookupKey) => bucket.lookupKeys.add(lookupKey));
            ridingNameLookupKeys.forEach((lookupKey) => bucket.lookupKeys.add(lookupKey));

            if (dep) {
              const ans = String(row[dep]);
              bucket.byResp[ans] = (bucket.byResp[ans] || 0) + rowWeight;
            }
          }
        });

        // attach % for coloring
        const grandProv = Object.values(prov).reduce((s, v) => s + v.total, 0);
        const grandRid = Object.values(rid).reduce((s, v) => s + v.total, 0);

        if (!isMounted) return;

        setProvCounts(
          Object.entries(prov).map(([provinceId, obj]) => ({
            province: Number(provinceId),
            total: obj.total,
            byResp: obj.byResp,
            freq: grandProv ? (obj.total / grandProv) * 100 : 0, // percent for choropleth
          }))
        );

        setRidCounts(
          Object.values(rid).map((obj) => ({
            riding: obj.riding,
            ridingName: obj.ridingName,
            lookupKeys: Array.from(obj.lookupKeys),
            total: obj.total,
            byResp: obj.byResp,
            freq: grandRid ? (obj.total / grandRid) * 100 : 0, // percent for choropleth
          }))
        );
      })
      .catch((error) => {
        if (isRequestAborted(error)) return;
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [
    dbName,
    dep,
    selectedFile,
    view,
    useWeightedData,
    weightColumn,
    isDemocracyCheckup,
    shouldUseDemocracyCheckupEndpoint,
    category,
  ]);

  const activeBuckets = useMemo(
    () => (view === "province" ? provCounts : ridCounts),
    [view, provCounts, ridCounts]
  );

  const mappedAnswerCodes = useMemo(() => new Set(Object.keys(ansMap || {}).map(String)), [ansMap]);

  const toBucketPoints = useMemo(
    () => (bucket) =>
      Object.entries(bucket?.byResp || {})
        .map(([answerCode, count]) => {
          const normalizedCode = String(answerCode).trim();
          if (!normalizedCode) return null;
          if (
            isCategoricalOutcome &&
            mappedAnswerCodes.size > 0 &&
            !mappedAnswerCodes.has(normalizedCode)
          ) {
            return null;
          }

          const numericCode = Number(normalizedCode);
          const numericCount = Number(count);
          if (
            !Number.isFinite(numericCode) ||
            !Number.isFinite(numericCount) ||
            numericCount <= 0
          ) {
            return null;
          }
          return { value: numericCode, weight: numericCount };
        })
        .filter(Boolean),
    [isCategoricalOutcome, mappedAnswerCodes]
  );

  const getBucketLabel = useMemo(
    () => (bucket, index) => {
      if (view === "province") {
        const provinceId = Number(bucket?.province);
        return provinceIdToName[provinceId] || `Province ${provinceId || index + 1}`;
      }

      const ridingId = Number(bucket?.riding);
      const lookupName = (bucket?.lookupKeys || [])
        .map((lookupKey) => normalizeRidingLookupKey(lookupKey))
        .map((normalizedLookupKey) => ridingNameByLookupKey[normalizedLookupKey] || "")
        .find(Boolean);
      const fallbackRidingName = Number.isFinite(ridingId)
        ? ridingNameById[String(ridingId)] || ""
        : "";
      const rawLabel = lookupName || bucket?.ridingName || fallbackRidingName || "";
      if (rawLabel) return toReadableLabel(rawLabel);
      if (Number.isFinite(ridingId)) return `Riding ${ridingId}`;
      return `Riding ${index + 1}`;
    },
    [view, ridingNameById, ridingNameByLookupKey]
  );

  const nationalStats = useMemo(() => {
    if (!dep) return null;
    const points = activeBuckets.flatMap((bucket) => toBucketPoints(bucket));
    if (!points.length) return null;

    return computeStatistics(points, "value", {
      weightAccessor: (point) => point.weight,
    });
  }, [dep, activeBuckets, toBucketPoints]);

  const indepStats = useMemo(() => {
    if (!dep) return [];

    return activeBuckets
      .map((bucket, index) => {
        const points = toBucketPoints(bucket);
        const stats = computeStatistics(points, "value", {
          weightAccessor: (point) => point.weight,
        });
        const totalWeight = points.reduce((sum, point) => sum + (Number(point.weight) || 0), 0);
        const frequencyByCode = new Map(
          points.map((point) => [
            String(Math.trunc(Number(point.value))),
            Number(point.weight) || 0,
          ])
        );
        const responseFrequencies = isCategoricalOutcome
          ? orderedResponseOptions.map((option) => {
              const count = frequencyByCode.get(option.code) || 0;
              return {
                code: option.code,
                label: option.label,
                count,
                pct: totalWeight > 0 ? (count / totalWeight) * 100 : 0,
              };
            })
          : [];

        return {
          label: getBucketLabel(bucket, index),
          ...stats,
          responseFrequencies,
        };
      })
      .filter((entry) => entry.sampleSize > 0);
  }, [
    dep,
    activeBuckets,
    toBucketPoints,
    getBucketLabel,
    isCategoricalOutcome,
    orderedResponseOptions,
  ]);

  /* ── 4.  UI ───────────────────────────────────────────────────────────────*/
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex flex-1 h-screen overflow-hidden">
        {/* sidebar */}
        <aside id="sidebar" className="w-80 p-6 bg-gray-50 border-r relative">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url(${backgroundImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="relative z-10 space-y-4">
            {/* Back to Home — same pattern as ChartForm */}
            <div className="mb-1">
              <a
                href="/"
                className="inline-flex items-center text-gray-700 hover:text-gray-900 focus:outline-none"
                aria-label="Back to Home"
              >
                <AiOutlineHome className="w-6 h-6" />
                <span className="text-[11px] leading-3 ml-1">Back to Home</span>
              </a>
            </div>

            {/* Study (replaces Year) */}
            <label className="block font-bold">Study</label>
            <select
              value={selectedFile?.name || ""}
              onChange={(e) => {
                const name = e.target.value;
                setSelectedFile(findStudyByName(studies, name));
                // optional: reset selections when switching study
                setDep("");
                setProvCounts([]);
                setRidCounts([]);
                setShowStats(false);
              }}
              className="w-full h-12 bg-gray-100 rounded-lg p-3 border"
            >
              <option value="">Select a Study</option>
              {studies.map((cat) => (
                <optgroup key={cat.key || cat.category} label={cat.category}>
                  {cat.files.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>

            {/* Outcome */}
            <label className="block font-bold">Outcome:</label>
            <select
              value={dep}
              onChange={(e) => setDep(e.target.value)}
              className="w-full h-12 bg-gray-100 rounded-lg p-3 border"
            >
              <option value="">What do you want to explore?</option>
              {depGroups.map((group) => (
                <optgroup key={group.group} label={group.group}>
                  {group.options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.question}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {dep && outcomeResponseEntries.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
                <p className="text-sm font-semibold text-[#181010]">Outcome Response Guide</p>
                {numericOutcomeGuide ? (
                  <div className="mt-2 space-y-1.5">
                    <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700">
                      {numericOutcomeGuide.scaleLabel}
                    </div>
                    <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700">
                      <strong>0:</strong> {numericOutcomeGuide.lowLabel}
                    </div>
                    <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700">
                      <strong>100:</strong> {numericOutcomeGuide.highLabel}
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 pr-1">
                    {outcomeResponseEntries.slice(0, 30).map((entry, index) => (
                      <div
                        key={`${entry}-${index}`}
                        className="rounded-md bg-gray-50 px-2.5 py-1.5 text-xs leading-4 text-gray-700"
                      >
                        {entry}
                      </div>
                    ))}
                    {outcomeResponseEntries.length > 30 && (
                      <p className="text-[11px] text-gray-500">
                        +{outcomeResponseEntries.length - 30} more responses
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* View mode */}
            <label className="block font-bold">Viewing mode</label>
            <select
              value={view}
              onChange={(e) => setView(e.target.value)}
              className="w-full h-12 bg-gray-100 rounded-lg p-3 border"
            >
              <option value="province">Province / Territory</option>
              <option value="riding">Riding</option>
            </select>

            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[#181010]">Weighted data</p>
                  <p className="text-xs text-gray-500">
                    Apply survey quota weights to map totals and frequencies.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useWeightedData}
                  onClick={() => setUseWeightedData((value) => !value)}
                  className={`relative inline-flex h-6 min-w-[42px] w-[42px] shrink-0 items-center overflow-hidden rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#181010]/20 ${
                    useWeightedData ? "bg-[#181010]" : "bg-gray-300"
                  }`}
                  aria-label="Toggle weighted data"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      useWeightedData ? "translate-x-[21px]" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* map */}
        <main className="flex-1 flex flex-col overflow-y-auto">
          <div className="relative h-[68vh] min-h-[520px]">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-lg font-semibold">Loading…</p>
              </div>
            ) : (
              <InteractiveMap
                viewMode={view}
                provinceCounts={provCounts}
                ridingCounts={ridCounts}
                answerDisplayMap={ansMap} /* ⇦ needed for tool-tips */
                outcomeKey={dep}
                useWeightedData={useWeightedData}
                depType={selectedDepMeta?.type || "cat"}
              />
            )}
          </div>

          <div className="px-6 pb-6">
            <ShowStatsSection
              canShowStats={activeRegionCount > 0 && Boolean(dep) && Boolean(nationalStats)}
              showStats={showStats}
              onToggleStats={() => setShowStats((value) => !value)}
            >
              <ChartStatisticsPanel
                nationalStats={nationalStats}
                selectedDepMeta={selectedDepMeta}
                dependentAnswerMapping={ansMap}
                sortBy={sortBy}
                setSortBy={setSortBy}
                indepStats={indepStats}
                selectedIndepBucket={selectedIndepBucket}
                setSelectedIndepBucket={setSelectedIndepBucket}
                indepIsGrouped={false}
                detectNumericScale={detectNumericScale}
                useWeightedData={useWeightedData}
                showCategoryResponseFrequencies={isCategoricalOutcome}
                categoryLabel={view === "province" ? "Province/Territory" : "Riding"}
              />
            </ShowStatsSection>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ViewInteractiveMapPage;
